import { RouteRecordRaw } from 'vue-router';

import { ROUTE_NAMES } from '@/constants';
import { RouteTab } from '@/models/routing';
import i18n, { loadTranslations } from '@/plugins/i18n';

import { authenticationGuard, fetchCommonData } from './guards';

// Replenishment Components
const ReplenishmentAdminView = () => import('@/pages/Admin/Replenishment/index.vue');
const RetailerManagement = () => import('@/pages/Admin/Replenishment/Retailer/RetailerManagement.vue');
const RetailerEdit = () => import('@/pages/Admin/Replenishment/RetailerEdit/EditRetailer.vue');
const OrderManagement = () => import('@/pages/Admin/Replenishment/Orders/OrderManagement.vue');
const ProductImport = () => import('@/pages/Admin/ProductImport/Upload.vue');
const IngestionList = () => import('@/pages/Admin/ProductImport/IngestionList.vue');
const IngestionDetail = () => import('@/pages/Admin/ProductImport/IngestionDetail.vue');
const OrderCoAdminView = () => import('@/pages/Admin/OrderCo/index.vue');
const OrderCoOrganizationManagement = () => import('@/pages/Admin/OrderCo/Organizations/OrderCoOrganizations.vue');
const MetrcLicenseInspector = () => import('@/pages/Admin/SeedToSale/MetrcLicenseInspector.vue');
const MetrcPackageInspector = () => import('@/pages/Admin/SeedToSale/MetrcPackageInspector.vue');

// Backfill Components
const WebhookBackfillManagement = () => import('@/pages/Admin/Backfill/WebhookManagement/index.vue');

const fetchTranslations = async (_to, _from) => {
  await loadTranslations('admin');
};

export const replenishmentAdminTabRoutes = () => {
  const routes = [
    {
      to: { name: ROUTE_NAMES.REPLENISHMENT_RETAILER_MANAGEMENT },
      label: i18n.t('admin.replenishment.retailer.tab'),
    },
    {
      to: { name: ROUTE_NAMES.REPLENISHMENT_ORDER_MANAGEMENT },
      label: i18n.t('common.orders'),
    },
  ];

  return Object.freeze(routes);
};

export function OrderCoAdminTabRoutes(): Readonly<RouteTab[]> {
  const routes = [
    {
      to: { name: ROUTE_NAMES.ORDER_CO_MANAGEMENT },
      label: i18n.t('common.organizations'),
    },
  ];

  return Object.freeze(routes);
}

const adminRoutes: RouteRecordRaw[] = [
  {
    path: '/ll/',
    meta: {
      permissions: [{ rules: [({ user }) => user.isLeaflinkStaff && user.isSuperuser] }],
    },
    children: [
      // Replenishment Routes
      {
        path: 'tools/replenishment/',
        component: ReplenishmentAdminView,
        beforeEnter: [authenticationGuard, fetchCommonData, fetchTranslations],
        meta: {
          tabs: replenishmentAdminTabRoutes,
        },
        children: [
          {
            path: '',
            redirect: { name: ROUTE_NAMES.REPLENISHMENT_RETAILER_MANAGEMENT },
          },
          {
            path: 'retailer-management',
            name: ROUTE_NAMES.REPLENISHMENT_RETAILER_MANAGEMENT,
            component: RetailerManagement,
            meta: {
              title: () => i18n.t('admin.replenishment.title'),
              description: () => i18n.t('admin.replenishment.description'),
            },
          },
          {
            path: 'order-management',
            name: ROUTE_NAMES.REPLENISHMENT_ORDER_MANAGEMENT,
            component: OrderManagement,
            meta: {
              title: () => i18n.t('admin.replenishment.title'),
              description: () => i18n.t('admin.replenishment.description'),
            },
          },
        ],
      },
      {
        path: 'tools/replenishment/retailer-management/:retailerId/edit',
        name: ROUTE_NAMES.REPLENISHMENT_RETAILER_MANAGEMENT_EDIT,
        props: true,
        beforeEnter: [authenticationGuard, fetchCommonData, fetchTranslations],
        component: RetailerEdit,
      },
      {
        path: 'import/products',
        name: ROUTE_NAMES.IMPORT_PRODUCTS,
        beforeEnter: [authenticationGuard, fetchCommonData, fetchTranslations],
        component: ProductImport,
      },
      {
        path: 'import/products/ingestion',
        name: ROUTE_NAMES.INGESTION_LIST,
        beforeEnter: [authenticationGuard, fetchCommonData, fetchTranslations],
        component: IngestionList,
      },
      {
        path: 'import/products/ingestion/:id',
        name: ROUTE_NAMES.INGESTION_ITEMS,
        beforeEnter: [authenticationGuard, fetchCommonData, fetchTranslations],
        component: IngestionDetail,
      },
      {
        path: 'tools/backfill/webhook-management',
        name: ROUTE_NAMES.WEBHOOK_BACKFILLS_MANAGEMENT,
        beforeEnter: [authenticationGuard, fetchCommonData, fetchTranslations],
        component: WebhookBackfillManagement,
      },
      // OrderCo
      {
        path: 'tools/order-co/',
        component: OrderCoAdminView,
        beforeEnter: [authenticationGuard, fetchCommonData, fetchTranslations],
        meta: {
          tabs: OrderCoAdminTabRoutes,
        },
        children: [
          {
            path: '',
            redirect: { name: ROUTE_NAMES.ORDER_CO_MANAGEMENT },
          },
          {
            path: 'organizations',
            name: ROUTE_NAMES.ORDER_CO_MANAGEMENT,
            component: OrderCoOrganizationManagement,
            meta: {
              title: () => i18n.t('admin.orderCo.organizations.title'),
              description: () => i18n.t('admin.orderCo.organizations.description'),
            },
          },
        ],
      },
      // Seed to Sale
      {
        path: 's2s/metrc-license-inspector/',
        component: MetrcLicenseInspector,
        beforeEnter: [authenticationGuard, fetchCommonData, fetchTranslations],
        // TODO: require the companies.change_company permission
      },
      {
        path: 's2s/metrc-package-inspector/',
        component: MetrcPackageInspector,
        beforeEnter: [authenticationGuard, fetchCommonData, fetchTranslations],
        // TODO: require the companies.change_company permission
      },
    ],
  },
];

export default adminRoutes;
